import { useState, useEffect, useCallback, useRef } from 'react';
import { X, FileText, Receipt, History } from 'lucide-react';
import { Button } from './ui';
import { type TabItem } from './CampaignFormTabs';
import { SponsorshipTab } from './campaign-form/SponsorshipTab';
import { CampaignActivityLog } from './CampaignActivityLog';
import type { CampaignWithComputed, CampaignStatus } from '../types/campaign';
import type { MohitInvoice, InfluencerInvoice, CampaignExpense } from '../types/campaign';
import { parseJsonArray } from '../lib/jsonColumn';
import { reportFirstMissingField } from '../utils/requiredFields';
import { campaignService } from '../services/campaignService';
import { clientService } from '../services/clientService';
import { invoiceService, netToInvoiceAmounts } from '../services/invoiceService';
import { toLocalDateOnly } from '../utils/dates';
import { invoiceLinkService, type InvoiceLink } from '../services/invoiceLinkService';
import { campaignAccessService } from '../services/campaignAccessService';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage, ValidationError } from '../contexts/LanguageContext';
import { useModal } from '../contexts/ModalContext';
import { ErrorMessage } from './ErrorMessage';
import { MohitInvoices } from './MohitInvoices';
import { InfluencerInvoices } from './InfluencerInvoices';
import { CampaignExpenses } from './CampaignExpenses';
import type { Client } from '../types/client';
import { log } from '../lib/logger';
import { AdminOnlyToggle } from './AdminOnlyToggle';

interface CampaignFormSponsorshipProps {
  campaign?: CampaignWithComputed | null;
  onClose: () => void;
  onSave: (saved?: CampaignWithComputed) => void;
}

export function CampaignFormSponsorship({ campaign, onClose, onSave }: CampaignFormSponsorshipProps) {
  const { user, isAdmin } = useAuth();
  const { t, translateError } = useLanguage();
  const { toast, showConfirm } = useModal();
  const currentYear = new Date().getFullYear();
  const [loading, setLoading] = useState(false);
  const [statusSaving, setStatusSaving] = useState(false);
  const [error, setError] = useState<{ message: string; technical: string } | null>(null);
  const agentName = user?.user_metadata?.full_name || user?.email || 'Desconhecido';

  const [formData, setFormData] = useState({
    year: campaign?.year || currentYear,
    work_code: campaign?.work_code || '',
    client: campaign?.client || '',
    brand: campaign?.brand || '',
    campaign_ref: campaign?.campaign_ref || '',
    agent_name: campaign?.agent_name || agentName,
    sponsorship_amount: (campaign as any)?.sponsorship_amount || 0,
    agency_commission_percent: (campaign as any)?.agency_commission_percent || 0.20,
    entitlements: (campaign as any)?.entitlements || '',
    event_date: (campaign as any)?.event_date || '',
    event_end_date: (campaign as any)?.event_end_date || '',
    campaign_status: (campaign?.campaign_status || 'Consultation') as CampaignStatus,
    sponsor_confirmed: (campaign as any)?.sponsor_confirmed || false,
    sponsor_confirmed_at: (campaign as any)?.sponsor_confirmed_at || '',
    admin_only: (campaign as any)?.admin_only ?? false,
  });

  const [activeTab, setActiveTab] = useState<string>('general');
  const [clients, setClients] = useState<string[]>([]);
  const [clientDetails, setClientDetails] = useState<Client | null>(null);
  const [clientInvoices, setClientInvoices] = useState<MohitInvoice[]>([]);
  const [payableInvoices, setPayableInvoices] = useState<InfluencerInvoice[]>([]);
  const [invoiceLinks, setInvoiceLinks] = useState<InvoiceLink[]>([]);
  const [campaignExpenses, setCampaignExpenses] = useState<CampaignExpense[]>(
    parseJsonArray<CampaignExpense>((campaign as any)?.campaign_expenses)
  );

  useEffect(() => {
    const loadData = async () => {
      try {
        const clientsData = await clientService.getAll();
        setClients(clientsData.map(c => c.name));

        if (campaign) {
          try {
            const links = await invoiceLinkService.getByCampaign(campaign.id);
            setInvoiceLinks(links);
          } catch (linkErr) {
            log.warn('Failed to load invoice links:', linkErr);
          }

          const allInvoices = await invoiceService.getByCampaign(campaign.id);
          const clientInvs = allInvoices.filter(inv => inv.type === 'Client');
          const payableInvs = allInvoices.filter(inv => inv.type === 'Influencer');

          const mohitInvoices: MohitInvoice[] = clientInvs.map(inv => ({
            id: inv.id,
            status: inv.status === 'Draft' ? 'Not Issued' : inv.status === 'Paid' ? 'Paid' : inv.status === 'Canceled' ? 'Cancelled' : 'Issued',
            invoice_number: inv.number || '',
            purchase_order: inv.notes?.startsWith('PO: ') ? inv.notes.slice(4) : '',
            issued_date: inv.issue_date || '',
            due_date: inv.due_date || '',
            amount_received: Number(inv.subtotal) || Number(inv.total) || 0,
            // paid_at is a timestamp; date inputs render blank unless given YYYY-MM-DD
            payment_received_date: inv.payments && inv.payments.length > 0 ? toLocalDateOnly(inv.payments[0].paid_at) : '',
            client_invoice_number: inv.number || '',
          }));

          const mappedPayables: InfluencerInvoice[] = payableInvs.map(inv => ({
            id: inv.id,
            invoice_number: inv.number || '',
            issue_date: inv.issue_date || '',
            due_date: inv.due_date || '',
            amount: Number(inv.subtotal) || Number(inv.total) || 0,
            status: inv.status === 'Draft'
              ? (inv.notes === 'STATUS: Requested' ? 'Requested' : 'Not Requested')
              : inv.status === 'Issued' ? 'Received' : inv.status === 'Paid' ? 'Paid' : 'Not Requested',
            payment_received_date: inv.payments && inv.payments.length > 0 ? toLocalDateOnly(inv.payments[0].paid_at) : '',
            source_name: inv.counterparty_name || '',
          }));

          setClientInvoices(mohitInvoices.length > 0 ? mohitInvoices : [{
            id: `temp-${crypto.randomUUID()}`,
            status: 'Not Issued',
            invoice_number: '',
            purchase_order: '',
            issued_date: '',
            due_date: '',
            amount_received: 0,
            payment_received_date: '',
            client_invoice_number: '',
          }]);

          setPayableInvoices(mappedPayables.length > 0 ? mappedPayables : [{
            id: `temp-${crypto.randomUUID()}`,
            invoice_number: '',
            issue_date: '',
            due_date: '',
            amount: 0,
            status: 'Not Requested',
            payment_received_date: '',
            source_name: '',
          }]);
        } else {
          // A new campaign starts without client invoices: they are requested later
          setClientInvoices([]);

          setPayableInvoices([{
            id: `temp-${crypto.randomUUID()}`,
            invoice_number: '',
            issue_date: '',
            due_date: '',
            amount: 0,
            status: 'Not Requested',
            payment_received_date: '',
            source_name: '',
          }]);
        }
      } catch (err) {
        log.error('Failed to load data:', err);
      }
      setSnapshotVersion(v => v + 1);
    };
    loadData();
  }, [campaign]);

  useEffect(() => {
    const loadClientDetails = async () => {
      if (formData.client) {
        const client = await clientService.getByName(formData.client);
        setClientDetails(client);
      } else {
        setClientDetails(null);
      }
    };
    loadClientDetails();
  }, [formData.client]);

  useEffect(() => {
    if (!campaign && formData.client && formData.year) {
      campaignService.generateNextWorkCode(formData.year).then(code => {
        setFormData(prev => ({ ...prev, work_code: code }));
      }).catch(err => {
        log.error('Failed to generate work code:', err);
      });
    }
  }, [formData.client, formData.year, campaign]);

  // ── Stay-open save: keep editing the record this form created ───────────
  const [persistedId, setPersistedId] = useState<string | null>(campaign?.id ?? null);

  // ── Unsaved changes guard ────────────────────────────────────────────────
  const serializeFormState = () => JSON.stringify({
    formData: { ...formData, work_code: undefined },
    clientInvoices, payableInvoices, campaignExpenses,
  });
  const dirtySnapshotRef = useRef<string | null>(null);
  const [snapshotVersion, setSnapshotVersion] = useState(0);
  useEffect(() => {
    dirtySnapshotRef.current = serializeFormState();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [snapshotVersion]);
  const isDirty = () => dirtySnapshotRef.current !== null && dirtySnapshotRef.current !== serializeFormState();

  const requestClose = async () => {
    if (isDirty()) {
      const discard = await showConfirm({
        title: t('form.discardChanges.title'),
        message: t('form.discardChanges.message'),
        confirmText: t('common.discard'),
        cancelText: t('common.cancel'),
        variant: 'warning',
      });
      if (!discard) return;
    }
    onClose();
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      // All of these live on the General tab — reveal it before highlighting
      const revealGeneral = () => setActiveTab('general');
      const missingRequired = reportFirstMissingField([
        {
          key: 'sponsorship-client',
          missing: !formData.client || formData.client.trim() === '',
          message: t('sponsorship.error.sponsorRequired'),
          reveal: revealGeneral,
        },
        {
          key: 'sponsorship-work-code',
          missing: (!formData.work_code || formData.work_code.trim() === '') && !!campaign,
          message: t('sponsorship.error.workCodeRequired'),
          reveal: revealGeneral,
        },
        {
          key: 'sponsorship-brand',
          missing: !formData.brand || formData.brand.trim() === '',
          message: t('sponsorship.error.eventNameRequired'),
          reveal: revealGeneral,
        },
        {
          key: 'sponsorship-event-date',
          missing: !formData.event_date,
          message: t('sponsorship.error.eventDateRequired'),
          reveal: revealGeneral,
        },
        {
          key: 'sponsorship-amount',
          missing: formData.sponsorship_amount <= 0,
          message: t('sponsorship.error.sponsorshipAmountRequired'),
          reveal: revealGeneral,
        },
      ], toast.error);
      if (missingRequired) {
        setLoading(false);
        return;
      }

      const missingPaymentDate = clientInvoices.filter(inv => inv.status === 'Paid' && !inv.payment_received_date);
      if (missingPaymentDate.length > 0) {
        const nums = missingPaymentDate.map(inv => inv.invoice_number || '(sem número)').join(', ');
        throw new ValidationError(`A fatura nº ${nums} está marcada como Paga mas não tem Data de Recebimento preenchida. Preencha a data para poder guardar.`);
      }

      try {
        const existingClient = await clientService.getByName(formData.client);
        if (!existingClient) {
          await clientService.create({
            name: formData.client,
            vat_number: null,
            address: null,
            contact_info: null
          });
        }
      } catch (clientErr) {
        log.warn('Auto-register client failed (likely permissions):', clientErr);
        toast.warning(t('warning.clientAutoRegisterFailed'));
      }

      let campaignId: string;

      const campaignData = {
        year: formData.year,
        work_code: formData.work_code,
        client: formData.client,
        brand: formData.brand,
        campaign_ref: formData.campaign_ref || null,
        agent_id: user?.id || null,
        agent_name: formData.agent_name,
        campaign_mode: 'sponsorship' as const,
        campaign_status: formData.campaign_status,
        sponsorship_amount: formData.sponsorship_amount,
        agency_commission_percent: formData.agency_commission_percent,
        entitlements: formData.entitlements || null,
        event_date: formData.event_date || null,
        event_end_date: formData.event_end_date || null,
        admin_only: formData.admin_only,
        campaign_expenses: JSON.stringify(campaignExpenses.filter(e => e.description || e.amount > 0)),
        influencer: '',
        fee: 0,
        commission_pct: 0,
        campaign_budget: 0,
        deliverables: '[]',
        delivery_date: null,
        delivery_status: 'Draft',
        ...(campaign ? {} : {
          company_invoice_status: 'Not Issued',
          influencer_invoice_status: 'Not Requested'
        })
      };

      const existingId = persistedId ?? campaign?.id ?? null;
      let savedRecord: CampaignWithComputed | null = null;
      if (existingId) {
        savedRecord = await campaignService.update(existingId, campaignData);
        campaignId = existingId;
      } else {
        (campaignData as any)._autoWorkCode = true;
        const newCampaign = await campaignService.create(campaignData);
        campaignId = newCampaign.id;
        savedRecord = newCampaign;
        setPersistedId(newCampaign.id);

        try {
          await campaignAccessService.grantAccessToAllUsers(campaignId);
        } catch (accessError) {
          log.error('Error granting access to users:', accessError);
        }
      }

      for (let i = 0; i < clientInvoices.length; i++) {
        const invoice = clientInvoices[i];
        const invoiceData = {
          campaign_id: campaignId,
          campaign_influencer_id: null,
          type: 'Client' as const,
          number: invoice.invoice_number || null,
          issue_date: invoice.issued_date || null,
          due_date: invoice.due_date || null,
          ...netToInvoiceAmounts(invoice.amount_received || 0),
          currency: 'EUR',
          status: invoice.status === 'Not Issued' ? 'Draft' as const : invoice.status === 'Paid' ? 'Paid' as const : invoice.status === 'Cancelled' ? 'Canceled' as const : 'Issued' as const,
          pdf_url: null,
          counterparty_name: formData.client,
          counterparty_email: null,
          notes: invoice.purchase_order ? `PO: ${invoice.purchase_order}` : null,
          display_order: i,
        };

        const isExisting = invoice.id && !invoice.id.startsWith('temp-') && invoice.id.length === 36 && invoice.id.includes('-');

        if (isExisting && campaign) {
          const existingInvoice = await invoiceService.getById(invoice.id);
          if (existingInvoice) {
            await invoiceService.update(invoice.id, invoiceData);
            await invoiceService.syncPaymentRecord(invoice.id, invoiceData.status, invoiceData.total, invoice.payment_received_date);
          } else {
            await invoiceService.create(invoiceData, invoice.payment_received_date || undefined);
          }
        } else {
          await invoiceService.create(invoiceData, invoice.payment_received_date || undefined);
        }
      }

      for (let i = 0; i < payableInvoices.length; i++) {
        const invoice = payableInvoices[i];
        const payableData = {
          campaign_id: campaignId,
          campaign_influencer_id: null,
          type: 'Influencer' as const,
          number: invoice.invoice_number || null,
          issue_date: invoice.issue_date || null,
          due_date: invoice.due_date || null,
          ...netToInvoiceAmounts(invoice.amount || 0),
          currency: 'EUR',
          status: invoice.status === 'Not Requested' ? 'Draft' as const : invoice.status === 'Paid' ? 'Paid' as const : invoice.status === 'Received' ? 'Issued' as const : 'Draft' as const,
          pdf_url: null,
          counterparty_name: invoice.source_name || null,
          counterparty_email: null,
          notes: invoice.status === 'Requested' ? 'STATUS: Requested' : null,
          display_order: i,
        };

        const isExisting = invoice.id && !invoice.id.startsWith('temp-') && invoice.id.length === 36 && invoice.id.includes('-');

        if (isExisting && campaign) {
          const existingInvoice = await invoiceService.getById(invoice.id);
          if (existingInvoice) {
            await invoiceService.update(invoice.id, payableData);
            await invoiceService.syncPaymentRecord(invoice.id, payableData.status, payableData.total, invoice.payment_received_date);
          } else {
            await invoiceService.create(payableData, invoice.payment_received_date || undefined);
          }
        } else {
          await invoiceService.create(payableData, invoice.payment_received_date || undefined);
        }
      }

      toast.success(existingId ? t('form.campaign.updateSuccess') : t('form.campaign.createSuccess'));
      // Stay open; the page refreshes the list and hands the record back
      onSave(savedRecord ?? undefined);
    } catch (err) {
      const technicalMessage = err instanceof Error ? err.message : 'Unknown error';
      const userMessage = translateError(err);
      setError({ message: userMessage, technical: technicalMessage });
    } finally {
      setLoading(false);
    }
  };

  const persistInvoiceOrder = useCallback(async (invoices: { id: string }[], type: 'Client' | 'Influencer') => {
    if (!campaign?.id) return;
    const existingIds = invoices
      .filter(inv => inv.id && !inv.id.startsWith('temp-') && inv.id.length === 36 && inv.id.includes('-'))
      .map(inv => inv.id);
    if (existingIds.length < 2) return;
    try {
      await invoiceService.reorderInvoices(campaign.id, type, existingIds);
    } catch {
    }
  }, [campaign?.id]);

  const handleClientInvoicesChange = useCallback((invoices: MohitInvoice[]) => {
    setClientInvoices(invoices);
    persistInvoiceOrder(invoices, 'Client');
  }, [persistInvoiceOrder]);

  const handlePayableInvoicesChange = useCallback((invoices: InfluencerInvoice[]) => {
    setPayableInvoices(invoices);
    persistInvoiceOrder(invoices, 'Influencer');
  }, [persistInvoiceOrder]);

  const handleCreateLink = useCallback(async (clientId: string, payableId: string) => {
    if (clientId.startsWith('temp-') || payableId.startsWith('temp-')) {
      toast.warning(t('xcheck.saveBeforeLinking'));
      return;
    }
    try {
      const link = await invoiceLinkService.create(clientId, payableId);
      // A payable can be funded by several receivables — append, don't replace.
      setInvoiceLinks(prev => [
        ...prev.filter(l => !(l.payable_invoice_id === payableId && l.client_invoice_id === clientId)),
        link,
      ]);
    } catch (err) {
      log.error('Failed to create invoice link:', err);
      toast.error(t('xcheck.linkFailed'));
    }
  }, [toast, t]);

  const handleRemoveLink = useCallback(async (clientId: string, payableId: string) => {
    try {
      await invoiceLinkService.deleteByPair(clientId, payableId);
      setInvoiceLinks(prev => prev.filter(l => !(l.client_invoice_id === clientId && l.payable_invoice_id === payableId)));
    } catch (err) {
      log.error('Failed to remove invoice link:', err);
      toast.error('Falha ao remover correlacao.');
    }
  }, [toast]);

  const handleFieldChange = (field: string, value: any) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleStatusChange = async (newStatus: CampaignStatus) => {
    setFormData(prev => ({ ...prev, campaign_status: newStatus }));

    const statusTargetId = persistedId ?? campaign?.id;
    if (statusTargetId) {
      setStatusSaving(true);
      try {
        await campaignService.update(statusTargetId, { campaign_status: newStatus });
        toast.success(t('sponsorship.statusUpdated'));
        onSave();
      } catch (err) {
        log.error('Failed to update status:', err);
        toast.error(t('sponsorship.statusUpdateFailed'));
        setFormData(prev => ({ ...prev, campaign_status: campaign?.campaign_status ?? prev.campaign_status }));
      } finally {
        setStatusSaving(false);
      }
    }
  };

  const tabs: TabItem[] = [
    {
      id: 'general',
      label: t('sponsorship.tab.general'),
      icon: FileText,
      badge: undefined,
      hasError: false,
    },
    {
      id: 'receivables',
      label: t('sponsorship.tab.receivables'),
      icon: Receipt,
      badge: clientInvoices.length,
      hasError: false,
    },
    {
      id: 'payables',
      label: t('sponsorship.tab.payables'),
      icon: Receipt,
      badge: payableInvoices.length,
      hasError: false,
    },
    ...(campaign ? [{
      id: 'activity',
      label: t('form.tab.activity'),
      icon: History,
      badge: undefined,
      hasError: false,
    }] : []),
  ];

  return (
    <div className="cm-shell cm-shell--fullpage fixed inset-0 z-50 flex flex-col bg-white">
        <div className="cm-header relative px-6 py-5 border-b border-gray-200">
          <div className="min-w-0 pr-12">
            <div className="flex items-center gap-3">
              <h2 className="cm-title text-xl font-semibold text-gray-900">
                {campaign ? t('sponsorship.editTitle') : t('sponsorship.newTitle')}
              </h2>
              <span className="cm-chip cm-chip-neutral">
                {t('sponsorship.badge')}
              </span>
            </div>
            {isAdmin && (
              <div className="mt-1.5">
                <AdminOnlyToggle
                  value={!!formData.admin_only}
                  onChange={(next) => setFormData(prev => ({ ...prev, admin_only: next }))}
                />
              </div>
            )}
          </div>
          <button
            type="button"
            onClick={requestClose}
            className="absolute top-4 right-4 p-2 text-gray-500 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors"
            aria-label="Close"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Inline tab bar */}
        <nav className="cm-tabbar flex items-center gap-1 px-4 border-b border-gray-200 overflow-x-auto">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              type="button"
              onClick={() => setActiveTab(tab.id)}
              className={`cm-tab ${activeTab === tab.id ? 'cm-tab--active' : ''}`}
            >
              {tab.icon && <tab.icon className="w-4 h-4" />}
              {tab.label}
              {tab.badge != null && (
                <span className="cm-tab-badge">{tab.badge}</span>
              )}
            </button>
          ))}
        </nav>

        <form onSubmit={handleSubmit} className="flex flex-col flex-1 min-h-0 bg-gray-50">
          <div className="cm-body flex-1 overflow-y-auto">
          {error && (
            <ErrorMessage
              message={error.message}
              technicalError={error.technical}
              className="m-8"
              onDismiss={() => setError(null)}
            />
          )}

          {activeTab === 'general' && (
            <SponsorshipTab
              formData={formData}
              clients={clients}
              isEditing={!!campaign}
              agentName={agentName}
              onChange={handleFieldChange}
              onStatusChange={handleStatusChange}
              statusSaving={statusSaving}
            />
          )}

          {activeTab === 'receivables' && (
            <div className="p-5 sm:p-8 pt-7 sm:pt-9">
              <MohitInvoices
                invoices={clientInvoices}
                onChange={handleClientInvoicesChange}
                totalCommission={formData.sponsorship_amount * formData.agency_commission_percent}
                requests={{
                  campaignId: persistedId,
                  campaignName: [formData.work_code, formData.brand].filter(Boolean).join(' · ') || 'Campanha',
                  purchaseOrder: clientInvoices.find(inv => inv.purchase_order)?.purchase_order,
                }}
                clientDetails={clientDetails}
                title={t('sponsorship.invoices.receivables')}
                totalAmount={formData.sponsorship_amount}
              />
            </div>
          )}

          {activeTab === 'payables' && (
            <div className="p-5 sm:p-8 pt-7 sm:pt-9 space-y-8">
              <InfluencerInvoices
                invoices={payableInvoices}
                onChange={handlePayableInvoicesChange}
                availableNames={[]}
                netFeeInfluencer={0}
                totalCommission={formData.sponsorship_amount * formData.agency_commission_percent}
                clientInvoices={clientInvoices}
                invoiceLinks={invoiceLinks}
                onCreateLink={(payableId, clientId) => handleCreateLink(clientId, payableId)}
                onRemoveLinkPair={(payableId, clientId) => handleRemoveLink(clientId, payableId)}
              />
              <CampaignExpenses expenses={campaignExpenses} onChange={setCampaignExpenses} />
            </div>
          )}

          {activeTab === 'activity' && campaign && (
            <div className="p-8">
              <CampaignActivityLog campaignId={campaign.id} />
            </div>
          )}
          </div>

          <div className="cm-footer flex justify-end gap-3 px-8 py-4 border-t border-gray-200 bg-white">
            <Button type="button" onClick={requestClose} variant="secondary" size="md" className="cm-btn cm-btn-secondary">
              {t('common.cancel')}
            </Button>
            <Button type="submit" disabled={loading} variant="primary" size="md" className="cm-btn cm-btn-primary">
              {loading ? t('sponsorship.saving') : campaign ? t('sponsorship.update') : t('sponsorship.create')}
            </Button>
          </div>
        </form>
    </div>
  );
}
